import React, { useState } from 'react';
import { Button, Select } from 'antd';

import GroupHeader from '../common/GroupHeader';
import RecordBadge from '../common/RecordBadge';
import { LIST, PARAGRAPH, PIN, PIN_ANGLE } from '../icons';
import { normalizeId } from '../../utils/ids';

// Add to Home's picker rows (AddToHomeModal): the square toggles a row pins
// with, and the Newest group — moved here from AddToHomeModal.js (KPR-1128),
// which is held at its line count by the file-size ratchet.

/** How many rows a group shows before "Show all". */
export const GROUP_LIMIT = 5;

/** The square Home pin toggle — the collection home's, in a picker row. */
export function PinToggle({ pinned, name, onToggle }) {
	return (
		<Button
			className={`home-pin-toggle${pinned ? ' home-pin-toggle--on' : ''}`}
			icon={pinned ? <PIN /> : <PIN_ANGLE />}
			aria-pressed={Boolean(pinned)}
			aria-label={`${pinned ? 'Unpin' : 'Pin'} ${name} ${pinned ? 'from' : 'to'} Home`}
			title={pinned ? 'On your Home — click to unpin' : 'Pin to your Home'}
			onClick={onToggle}
		/>
	);
}

/**
 * "Pin as tile…" beside a row's pin toggle (KPR-1127): opens the element
 * picker for a tile of the row's record (or of the newest record of its
 * card). Pressed when that tile is on Home already; the picker then edits it.
 */
export function TileToggle({ tiled, name, onOpen }) {
	return (
		<Button
			className={`home-pin-toggle home-tile-toggle${tiled ? ' home-pin-toggle--on' : ''}`}
			icon={<PARAGRAPH />}
			aria-pressed={Boolean(tiled)}
			aria-label={`${tiled ? 'Edit the Home tile of' : 'Pin as tile:'} ${name}`}
			title={tiled ? 'A tile on your Home — click to change it' : 'Pin as a tile of its values'}
			onClick={onOpen}
		/>
	);
}

/**
 * "Pin as a list…" beside a Newest row's toggles (KPR-1128): opens the count
 * picker for a short list of the card's records under the filter chosen.
 * Pressed when that list is on Home already; the picker then edits it.
 */
export function ListToggle({ listed, name, onOpen }) {
	return (
		<Button
			className={`home-pin-toggle home-list-toggle${listed ? ' home-pin-toggle--on' : ''}`}
			icon={<LIST />}
			aria-pressed={Boolean(listed)}
			aria-label={`${listed ? 'Edit the Home list of' : 'Pin as a list:'} ${name}`}
			title={listed ? 'A list on your Home — click to change it' : 'Pin as a list of its first records'}
			onClick={onOpen}
		/>
	);
}

const cardSingular = card => card?.label?.singular || card?.name || 'item';
const cardPlural = card => card?.label?.plural || card?.name || 'items';

/**
 * One "Newest <card>" row (KPR-1126): the card's badge, a picker of the
 * collection's saved filters ("Every <card>" — none — first) and the pin
 * toggle for the card under the filter chosen. Which record it opens is
 * asked when the pin is clicked, never here. The tile button pins a tile of
 * its values (KPR-1127), the list button a short list of the card's records
 * (KPR-1128), both under the filter chosen.
 */
function NewestRow({ card, filters, collectionId, isPinned, onToggle, isTiled, onTile, isListed, onList }) {
	const [filterId, setFilterId] = useState('');
	const cardId = normalizeId(card._id);
	const filter = filters.find(entry => normalizeId(entry._id) === filterId) || null;
	const name = filter ? `Newest ${cardSingular(card)}, ${filter.name}` : `Newest ${cardSingular(card)}`;
	const listName = filter ? (filter.name || 'Untitled filter') : cardPlural(card);
	return (
		<li className="add-to-home__row add-to-home__row--newest">
			<span className="add-to-home__face">
				<RecordBadge kind="card" record={card} kicker="Newest" title={card.name || 'Untitled card'} size="sm" />
			</span>
			{filters.length ? (
				<Select
					size="small"
					className="add-to-home__filter"
					aria-label={`Saved filter for the newest ${cardSingular(card)}`}
					value={filterId}
					onChange={value => setFilterId(value || '')}
					popupMatchSelectWidth={false}
					options={[
						{ value: '', label: `Every ${cardSingular(card)}` },
						...filters.map(entry => ({ value: normalizeId(entry._id), label: entry.name || 'Untitled filter' })),
					]}
				/>
			) : null}
			<PinToggle
				pinned={isPinned(cardId, filterId || null)}
				name={name}
				onToggle={() => onToggle(cardId, filterId || null, collectionId)}
			/>
			{onTile ? (
				<TileToggle
					tiled={isTiled(cardId, filterId || null)}
					name={name}
					onOpen={() => onTile({ card, filterId: filterId || null, name })}
				/>
			) : null}
			{onList ? (
				<ListToggle
					listed={isListed(cardId, filterId || null)}
					name={listName}
					onOpen={() => onList({ card, filterId: filterId || null, name: listName })}
				/>
			) : null}
		</li>
	);
}

/** The Newest group: a row per card, a few at first, then "Show all N cards". */
export function NewestGroup({ cards, filters, collectionId, isPinned, onToggle, isTiled, onTile, isListed, onList }) {
	const [expanded, setExpanded] = useState(false);
	if (!cards.length) return null;
	const shown = expanded ? cards : cards.slice(0, GROUP_LIMIT);
	return (
		<section className="add-to-home__group" aria-label="Newest">
			<GroupHeader label="Newest" count={cards.length} />
			<ul className="add-to-home__rows">
				{shown.map(card => (
					<NewestRow
						key={normalizeId(card._id)}
						card={card}
						filters={filters}
						collectionId={collectionId}
						isPinned={isPinned}
						onToggle={onToggle}
						isTiled={isTiled}
						onTile={onTile}
						isListed={isListed}
						onList={onList}
					/>
				))}
			</ul>
			{cards.length > GROUP_LIMIT ? (
				<Button type="link" size="small" className="add-to-home__more" onClick={() => setExpanded(open => !open)}>
					{expanded ? 'Show fewer' : `Show all ${cards.length} cards`}
				</Button>
			) : null}
		</section>
	);
}
